/**
 * F29 — SentEmailsPanel: lista de correos enviados desde JR2 OS asociados a
 * un cliente o a una oportunidad, con badge de aperturas (pixel de tracking).
 *
 * ☠️ Por defecto NO enseña las campañas de marketing, aunque cada destinatario
 * de una campaña sea técnicamente un correo enviado a ese cliente. Lo que se
 * busca en la ficha de alguien es lo que se le escribió A ÉL; doce boletines
 * mezclados por fecha empujan fuera de la lista la correspondencia de verdad.
 * El conmutador las trae, y entonces cada una lleva su campaña escrita.
 */

import { useCallback, useEffect, useState } from 'react'
import { Loader2, Mail, MailCheck, AlertCircle, Eye, Megaphone, Paperclip } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { api, ApiError } from '../../lib/api'
import { currentLocaleTag } from '../../lib/formatting'

interface SentItem {
  id: string
  fromEmail: string
  toAddresses: string[]
  ccAddresses: string[]
  bccAddresses: string[]
  subject: string
  sentAt: string
  errorMessage: string | null
  clientId: string | null
  opportunityId: string | null
  attachmentCount: number
  senderName: string | null
  openCount: number
  lastOpenedAt: string | null
  campaignId: string | null
  campaignName: string | null
}

interface SentListResponse {
  items: SentItem[]
  total: number
  page: number
  pageSize: number
}

interface Props {
  clientId?: string
  opportunityId?: string
  /** Cambia este número desde el padre para forzar un refresh tras enviar. */
  refreshKey?: number
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString(currentLocaleTag(), {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function SentEmailsPanel({ clientId, opportunityId, refreshKey }: Props) {
  const { t } = useTranslation('email')
  const [data, setData] = useState<SentListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [conCampanas, setConCampanas] = useState(false)

  const load = useCallback(async () => {
    if (!clientId && !opportunityId) return
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (clientId) params.set('clientId', clientId)
      if (opportunityId) params.set('opportunityId', opportunityId)
      params.set('pageSize', '25')
      if (conCampanas) params.set('includeCampaigns', 'true')
      const res = await api.get<SentListResponse>(`/emails/sent?${params.toString()}`)
      setData(res)
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | undefined
        setError(body?.error ?? `Error ${err.status}`)
      } else {
        setError(i18n.t('email:errors.network'))
      }
    } finally {
      setLoading(false)
    }
  }, [clientId, opportunityId, conCampanas])

  useEffect(() => {
    void load()
  }, [load, refreshKey])

  return (
    <div className="surface p-4">
      <h3 className="t-primary font-semibold mb-3 flex items-center gap-2">
        <Mail size={16} className="text-jr2-700 dark:text-jr2-300" />
        {t('sent.title')}
        {data && data.total > 0 && (
          <span className="text-xs t-faint font-normal">({data.total})</span>
        )}
        <button
          type="button"
          onClick={() => setConCampanas((v) => !v)}
          className={`ml-auto text-[11px] font-normal px-2 py-1 rounded-full border transition ${
            conCampanas
              ? 'bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-200 border-fuchsia-500/40'
              : 't-muted border-[var(--border)]/70 hover:bg-hover'
          }`}
        >
          {conCampanas ? t('sent.hideCampaigns') : t('sent.showCampaigns')}
        </button>
      </h3>

      {loading && (
        <div className="flex items-center gap-2 text-sm t-muted py-3">
          <Loader2 size={14} className="animate-spin" /> {t('sent.loading')}
        </div>
      )}

      {error && (
        <div className="text-sm text-rose-700 dark:text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {!loading && !error && data && data.items.length === 0 && (
        <p className="text-sm t-faint py-2">{t('sent.empty')}</p>
      )}

      {!loading && !error && data && data.items.length > 0 && (
        <ul className="space-y-2">
          {data.items.map((it) => (
            <li
              key={it.id}
              className="border border-[var(--border)]/70 rounded-lg p-3 bg-[var(--bg-input-soft)] hover:bg-slate-900/60 transition"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="t-primary text-sm font-medium truncate flex items-center gap-1.5">
                    {it.errorMessage ? (
                      <AlertCircle size={14} className="text-rose-400 shrink-0" />
                    ) : (
                      <MailCheck size={14} className="text-emerald-400 shrink-0" />
                    )}
                    {it.subject || t('sent.noSubject')}
                    {it.campaignName && (
                      <span
                        className="shrink-0 inline-flex items-center gap-1 text-[10px] font-normal px-1.5 py-0.5 rounded-full bg-fuchsia-500/15 text-fuchsia-700 dark:text-fuchsia-200 border border-fuchsia-500/40"
                        title={it.campaignName}
                      >
                        <Megaphone size={9} /> {t('sent.campaignTag')}
                      </span>
                    )}
                  </div>
                  <div className="t-muted text-xs mt-0.5 truncate">
                    {t('sent.to')} {it.toAddresses.join(', ')}
                    {it.ccAddresses.length > 0 && ` · ${t('sent.cc')} ${it.ccAddresses.join(', ')}`}
                  </div>
                  <div className="t-faint text-[11px] mt-1 flex items-center gap-2 flex-wrap">
                    <span>{formatDate(it.sentAt)}</span>
                    {it.senderName && <span>· {it.senderName}</span>}
                    {it.attachmentCount > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <Paperclip size={10} /> {it.attachmentCount}
                      </span>
                    )}
                    {it.errorMessage && (
                      <span className="text-rose-700 dark:text-rose-300">· {it.errorMessage}</span>
                    )}
                  </div>
                </div>
                <div className="shrink-0">
                  <OpenBadge count={it.openCount} lastOpenedAt={it.lastOpenedAt} />
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function OpenBadge({
  count,
  lastOpenedAt,
}: {
  count: number
  lastOpenedAt: string | null
}) {
  const { t } = useTranslation('email')
  if (count === 0) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-slate-800/60 text-slate-400 border border-[var(--border)]/70"
        title={t('openBadge.noneTitle')}
      >
        <Eye size={10} /> {t('openBadge.none')}
      </span>
    )
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-jr2-500/15 text-jr2-700 dark:text-jr2-200 border border-jr2-500/30 font-medium"
      title={lastOpenedAt ? `${t('openBadge.lastLabel')}: ${new Date(lastOpenedAt).toLocaleString(currentLocaleTag())}` : undefined}
    >
      <Eye size={10} /> {count} {count === 1 ? t('openBadge.openSingular') : t('openBadge.openPlural')}
    </span>
  )
}
